import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, X, MapPin } from 'lucide-react';
import {
  searchLocationsForTypeahead,
  type LocationTypeaheadRow,
} from '@/lib/api';
import formStyles from '@/components/Form.module.css';
import styles from './LocationTypeahead.module.css';

/**
 * Location-search typeahead used by the resident-managed "My Chargers
 * → Add" flow. Mirrors the admin-side `UserTypeahead` pattern (chip
 * row + debounced input + results panel) but tuned for single-select
 * and with the location-row affordances (name + address line) the
 * driver-side flow needs to confirm "yes, this is my building."
 *
 * Visibility: results come back from the server-side
 * `GET /locations/typeahead`, which surfaces the caller's own tenant
 * + any location with public_network+open chargers. The driver picks
 * the building they're physically at; the registration endpoint
 * (`POST /chargers/resident-register`) re-verifies the building's
 * `allow_resident_self_registration` opt-in server-side, so this
 * component intentionally does NOT pre-filter on opt-in (the server
 * is the source of truth and the surface here is just a discovery
 * aid).
 */

export interface LocationTypeaheadProps {
  /** ObjectId string of the currently-selected location, if any. */
  selectedId: string | null;
  /** Called with the next selection (or null to clear). */
  onChange: (id: string | null, row: LocationTypeaheadRow | null) => void;
  placeholder?: string;
  disabled?: boolean;
  ariaLabel?: string;
}

export function LocationTypeahead({
  selectedId,
  onChange,
  placeholder,
  disabled = false,
  ariaLabel,
}: LocationTypeaheadProps) {
  const { t } = useTranslation('typeaheads');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<LocationTypeaheadRow[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  // Display-side cache so the chip can render the location's name/
  // address without a follow-up fetch after picking. Source of truth
  // remains the caller's selectedId; this is purely hydration.
  const [rowCache, setRowCache] = useState<Map<string, LocationTypeaheadRow>>(
    () => new Map(),
  );
  const timer = useRef<number | null>(null);

  // Debounced search. Skips when query empty OR when a selection is
  // already locked in (the chip replaces the input UI).
  useEffect(() => {
    if (selectedId) {
      setResults([]);
      return;
    }
    if (!query.trim()) {
      setResults([]);
      return;
    }
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      setSearching(true);
      void searchLocationsForTypeahead(query)
        .then((rows) => {
          setResults(rows);
          setRowCache((prev) => {
            const next = new Map(prev);
            for (const r of rows) next.set(r._id, r);
            return next;
          });
        })
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 220);
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, [query, selectedId]);

  const handlePick = (row: LocationTypeaheadRow): void => {
    setRowCache((prev) => {
      const next = new Map(prev);
      next.set(row._id, row);
      return next;
    });
    onChange(row._id, row);
    setQuery('');
    setOpen(false);
    setResults([]);
  };

  const handleClear = (): void => {
    onChange(null, null);
  };

  const selectedRow = useMemo(
    () => (selectedId ? rowCache.get(selectedId) ?? null : null),
    [selectedId, rowCache],
  );

  return (
    <div className={styles.wrap}>
      {selectedId && (
        <div className={styles.chip}>
          <MapPin size={14} className={styles.chipIcon} aria-hidden />
          <span className={styles.chipBody}>
            <span className={styles.chipName}>
              {selectedRow?.name ?? t('location.selectedFallback')}
            </span>
            {selectedRow?.address && (
              <span className={styles.chipAddress}>
                {selectedRow.address.street}, {selectedRow.address.city},{' '}
                {selectedRow.address.state}
              </span>
            )}
          </span>
          <button
            type="button"
            className={styles.chipClear}
            onClick={handleClear}
            disabled={disabled}
            aria-label={t('location.clearSelection')}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {!selectedId && (
        <div className={styles.pickerWrap}>
          <input
            className={formStyles.input}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder ?? t('location.searchPlaceholder')}
            disabled={disabled}
            autoComplete="off"
            aria-label={ariaLabel ?? t('location.searchAria')}
          />
          {open && !disabled && (query.trim() || searching) && (
            <div className={styles.resultsPanel}>
              {searching && (
                <div className={styles.resultEmpty}>
                  <Loader2 size={12} className={styles.spin} /> {t('location.searching')}
                </div>
              )}
              {!searching && results.length === 0 && query.trim() && (
                <div className={styles.resultEmpty}>{t('location.noMatches')}</div>
              )}
              {!searching &&
                results.map((row) => (
                  <button
                    key={row._id}
                    type="button"
                    className={styles.resultRow}
                    onClick={() => handlePick(row)}
                  >
                    <span className={styles.resultPrimary}>{row.name}</span>
                    <span className={styles.resultSecondary}>
                      {row.address.street}, {row.address.city},{' '}
                      {row.address.state}
                    </span>
                  </button>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
